import React, { useState } from 'react'

interface TestResult {
  endpoint: string
  status: number
  success: boolean
  data?: any
  error?: string
}

export function LoginDebug() {
  const [username, setUsername] = useState('mariusz')
  const [password, setPassword] = useState('nrgH TCNf bX2L SgNQ jCJY ibzu')
  const [results, setResults] = useState<TestResult[]>([])
  const [testing, setTesting] = useState(false)

  const testEndpoint = async (endpoint: string, name: string): Promise<TestResult> => {
    try {
      const credentials = btoa(`${username}:${password}`)
      const response = await fetch(`https://admin.reddminer.com${endpoint}`, {
        method: 'GET',
        headers: {
          'Authorization': `Basic ${credentials}`,
          'Content-Type': 'application/json'
        }
      })

      const responseText = await response.text()
      let data
      try {
        data = JSON.parse(responseText)
      } catch {
        data = { raw: responseText }
      }

      return {
        endpoint: `${name} (${endpoint})`,
        status: response.status,
        success: response.ok,
        data
      }
    } catch (error) {
      return {
        endpoint: `${name} (${endpoint})`,
        status: 0,
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  const runTests = async () => {
    setTesting(true)
    setResults([])

    const endpoints = [
      { endpoint: '/?rml_auth=custom', name: 'Custom Auth Endpoint (RECOMMENDED)' },
      { endpoint: '/wp-json/wp/v2/users/me', name: 'WordPress Users/Me' },
      { endpoint: '/wp-json/wp/v2/', name: 'WordPress Root' },
      { endpoint: '/wp-json/redd-miner-leads/v1/user/profile', name: 'Custom RML Profile' }
    ]

    const testResults: TestResult[] = []

    for (const { endpoint, name } of endpoints) {
      const result = await testEndpoint(endpoint, name)
      testResults.push(result)
      setResults([...testResults])
      await new Promise(resolve => setTimeout(resolve, 1000)) // Delay between tests
    }

    setTesting(false)
  }

  const getStatusColor = (result: TestResult) => {
    if (result.success) return 'border-green-500 bg-green-50'
    if (result.status === 503) return 'border-yellow-500 bg-yellow-50'
    if (result.status === 401) return 'border-red-500 bg-red-50'
    if (result.error) return 'border-purple-500 bg-purple-50'
    return 'border-gray-500 bg-gray-50'
  }

  const getStatusIcon = (result: TestResult) => {
    if (result.success) return '✅'
    if (result.status === 503) return '⏳'
    if (result.status === 401) return '🔒'
    if (result.error) return '🌐'
    return '❌'
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white p-6 rounded-lg shadow mb-8">
          <h1 className="text-2xl font-bold mb-2">🚨 Login Authentication Debugger</h1>
          <p className="text-gray-600 mb-6">
            Test different API endpoints to identify authentication issues
          </p>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="WordPress username"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Application Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="WordPress application password"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <button
              onClick={runTests}
              disabled={testing || !username || !password}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {testing ? '🔄 Testing...' : '🧪 Run Authentication Tests'}
            </button>
          </div>
        </div>

        {results.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Test Results</h2>
            {results.map((result, index) => (
              <div key={index} className={`border-2 p-4 rounded-lg ${getStatusColor(result)}`}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium">
                    {getStatusIcon(result)} {result.endpoint}
                  </h3>
                  <span className="text-sm font-mono">
                    Status: {result.status || 'Network Error'}
                  </span>
                </div>

                {result.success && (
                  <div className="text-green-700">
                    <p className="font-medium">✅ SUCCESS! This endpoint works!</p>
                    {result.data?.id && (
                      <p className="text-sm">User ID: {result.data.id}, Username: {result.data.slug || result.data.username}</p>
                    )}
                  </div>
                )}

                {result.status === 503 && (
                  <div className="text-yellow-700">
                    <p className="font-medium">⏳ Server temporarily overloaded</p>
                    <p className="text-sm">Your credentials are likely correct, just server issues</p>
                  </div>
                )}

                {result.status === 401 && (
                  <div className="text-red-700">
                    <p className="font-medium">🔒 Authentication failed</p>
                    <p className="text-sm">Check username and application password</p>
                  </div>
                )}

                {result.error && (
                  <div className="text-purple-700">
                    <p className="font-medium">🌐 Network Error: {result.error}</p>
                    <p className="text-sm">Could be CORS or connectivity issue</p>
                  </div>
                )}

                <details className="mt-2">
                  <summary className="text-sm cursor-pointer text-gray-600">
                    View Response Data
                  </summary>
                  <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-auto">
                    {JSON.stringify(result.data, null, 2)}
                  </pre>
                </details>
              </div>
            ))}

            {results.some(r => r.success) && (
              <div className="border-2 border-green-500 bg-green-50 p-4 rounded-lg">
                <h3 className="font-medium text-green-800 mb-2">🎉 Solution Found!</h3>
                <p className="text-green-700">
                  At least one endpoint is working. The authentication issue might be:
                </p>
                <ul className="list-disc list-inside text-sm text-green-700 mt-2">
                  <li>Browser cache (try Ctrl+Shift+R)</li>
                  <li>Incognito/private mode</li>
                  <li>JavaScript errors in console</li>
                  <li>Old build files not uploaded</li>
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="mt-8 border-2 border-blue-500 bg-blue-50 p-4 rounded-lg">
          <h3 className="font-medium text-blue-800 mb-2">🔍 Browser Network Debug</h3>
          <p className="text-blue-700 text-sm">
            Open Developer Tools (F12) → Network tab, then try logging in on the main site to see actual network requests.
          </p>
          <a
            href="https://reddminer.com/login"
            target="_blank"
            className="inline-block mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            🔗 Test Main Login Page
          </a>
        </div>
      </div>
    </div>
  )
}

export default LoginDebug